<template>
	<SuiSelectList v-if="!!result">
		<template #header>
			<transition name="sui-select-list-items-view-switch">
				<SuiSelectListSearch
					v-model="searchPhrase"
					:placeholder="$t('bookmarks_dropdown_search_placeholder')"
				/>
			</transition>
		</template>

		<SuiSelectListHeader v-if="filteredMyBookmarksList.length">
			{{ $t('bookmarks_dropdown_my_bookmarks') }}
		</SuiSelectListHeader>

		<SuiSelectListButton
			v-for="item in filteredMyBookmarksList"
			:key="item.id"
			is-bold
			@click="$emit('update:modelValue', item)"
		>
			{{ item.name }}
		</SuiSelectListButton>

		<SuiSelectListHeader v-if="filteredOtherBookmarksList.length">
			{{ $t('bookmarks_dropdown_other_bookmarks') }}
		</SuiSelectListHeader>

		<SuiSelectListButton
			v-for="item in filteredOtherBookmarksList"
			:key="item.id"
			is-bold
			@click="$emit('update:modelValue', item)"
		>
			<SuiInitials
				v-tooltip="item.owner.name"
				class="bookmarks-dropdown-select-list__initials"
				type="arrow"
				as="div"
			>
				{{ item.owner.initials }}
			</SuiInitials>
			<span>{{ item.name }}</span>
		</SuiSelectListButton>

		<SuiSelectListEmpty v-if="isListEmpty">
			{{ $t('bookmarks_dropdown_no_results') }}
		</SuiSelectListEmpty>
	</SuiSelectList>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
	SuiInitials,
	SuiSelectList,
	SuiSelectListButton,
	SuiSelectListEmpty,
	SuiSelectListHeader,
	SuiSelectListSearch,
} from '@developers/scoro-ui';
import {
	useBookmarksDropdownQuery,
	type BookmarksDropdownQuery,
} from '@/generated/graphql';
import { useCurrentUser } from '@/features/Auth';
import { caseInsensitiveSearch } from '@/utils/misc';

defineEmits<{
	(
		e: 'update:modelValue',
		bookmark: BookmarksDropdownQuery['BookmarksList'][0]
	): void;
}>();

const props = defineProps<{
	modules?: string[] | undefined;
}>();

const { currentUser } = useCurrentUser();
const { result } = useBookmarksDropdownQuery({
	filters: {
		modules: props.modules,
		isShared: true,
	},
});
const searchPhrase = ref('');

const bookmarks = computed(() => {
	return result.value?.BookmarksList || [];
});

const filteredMyBookmarksList = computed(() => {
	return caseInsensitiveSearch(
		bookmarks.value,
		searchPhrase.value,
		'name'
	).filter((item) => item.owner.id === currentUser.value?.id);
});

const filteredOtherBookmarksList = computed(() => {
	return caseInsensitiveSearch(
		bookmarks.value,
		searchPhrase.value,
		'name'
	).filter((item) => item.owner.id !== currentUser.value?.id);
});

const isListEmpty = computed(
	() =>
		filteredMyBookmarksList.value.length === 0 &&
		filteredOtherBookmarksList.value.length === 0
);
</script>

<style lang="scss">
.bookmarks-dropdown-select-list {
	&__initials {
		min-width: 4rem;
	}
}
</style>
